import { useState } from 'react';
import { Plus, Pencil, Trash2, Copy, Tag } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { toast } from 'sonner';
import { API } from './constants';

export default function CouponsTab({ coupons, onRefresh }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [form, setForm] = useState({
    code: '', discount_type: 'percent', discount_value: 10, min_purchase: 0,
    max_uses: '', valid_from: '', valid_until: '', company: ''
  });

  const resetForm = () => {
    setForm({
      code: '', discount_type: 'percent', discount_value: 10, min_purchase: 0,
      max_uses: '', valid_from: '', valid_until: '', company: ''
    });
    setEditingCoupon(null);
  };

  const handleEdit = (coupon) => {
    setEditingCoupon(coupon);
    setForm({
      code: coupon.code,
      discount_type: coupon.discount_type,
      discount_value: coupon.discount_value,
      min_purchase: coupon.min_purchase || 0,
      max_uses: coupon.max_uses || '',
      valid_from: coupon.valid_from || '',
      valid_until: coupon.valid_until || '',
      company: coupon.company || ''
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const url = editingCoupon 
        ? `${API}/admin/coupons/${editingCoupon.coupon_id}`
        : `${API}/admin/coupons`;
      
      const response = await fetch(url, {
        method: editingCoupon ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...form,
          max_uses: form.max_uses ? parseInt(form.max_uses) : null
        })
      });

      if (response.ok) {
        toast.success(editingCoupon ? 'Cupón actualizado' : 'Cupón creado');
        setDialogOpen(false);
        resetForm();
        onRefresh?.();
      } else {
        toast.error('Error al guardar cupón');
      }
    } catch (err) {
      toast.error('Error de conexión');
    }
  };

  const handleDelete = async (couponId) => {
    if (!window.confirm('¿Eliminar este cupón?')) return;
    
    try {
      const response = await fetch(`${API}/admin/coupons/${couponId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Cupón eliminado');
        onRefresh?.();
      }
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Código copiado');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-stone-900">Gestión de Cupones</h1>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="bg-emerald-700 hover:bg-emerald-800 rounded-full"
              onClick={resetForm}
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Cupón
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingCoupon ? 'Editar Cupón' : 'Nuevo Cupón'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium text-stone-700">Código *</label>
                <Input
                  value={form.code}
                  onChange={(e) => setForm({...form, code: e.target.value.toUpperCase()})}
                  className="mt-1"
                  placeholder="DESCUENTO10"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-stone-700">Tipo de Descuento</label>
                  <Select value={form.discount_type} onValueChange={(v) => setForm({...form, discount_type: v})}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percent">Porcentaje (%)</SelectItem>
                      <SelectItem value="fixed">Monto Fijo ($)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-700">Valor</label>
                  <Input
                    type="number"
                    value={form.discount_value}
                    onChange={(e) => setForm({...form, discount_value: parseFloat(e.target.value)})}
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-stone-700">Compra Mínima</label>
                  <Input
                    type="number"
                    value={form.min_purchase}
                    onChange={(e) => setForm({...form, min_purchase: parseFloat(e.target.value)})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-700">Usos Máximos</label>
                  <Input
                    type="number"
                    value={form.max_uses}
                    onChange={(e) => setForm({...form, max_uses: e.target.value})}
                    className="mt-1"
                    placeholder="Sin límite"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-stone-700">Válido Desde</label>
                  <Input
                    type="date"
                    value={form.valid_from}
                    onChange={(e) => setForm({...form, valid_from: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-700">Válido Hasta</label>
                  <Input
                    type="date"
                    value={form.valid_until}
                    onChange={(e) => setForm({...form, valid_until: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700">Empresa (opcional)</label>
                <Input
                  value={form.company}
                  onChange={(e) => setForm({...form, company: e.target.value})}
                  className="mt-1"
                  placeholder="Restringir a empresa específica"
                />
              </div>
              <Button onClick={handleSave} className="w-full bg-emerald-700 hover:bg-emerald-800">
                {editingCoupon ? 'Actualizar' : 'Crear'} Cupón
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Coupons List */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {coupons.map(coupon => (
          <div key={coupon.coupon_id} className="bg-white rounded-2xl border border-stone-200 p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <Tag className="w-5 h-5 text-emerald-700" />
                </div>
                <div>
                  <p className="font-mono font-bold text-lg text-stone-900">{coupon.code}</p>
                  <p className="text-sm text-emerald-700">
                    {coupon.discount_type === 'percent' ? `${coupon.discount_value}% desc.` : `$${coupon.discount_value} desc.`}
                  </p>
                </div>
              </div>
              <Badge className={coupon.active ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-100 text-stone-500'}>
                {coupon.active ? 'Activo' : 'Inactivo'}
              </Badge>
            </div>

            <div className="space-y-1 text-sm text-stone-500 mb-4">
              {coupon.min_purchase > 0 && <p>Mín: ${coupon.min_purchase}</p>}
              {coupon.max_uses && <p>Usos: {coupon.uses_count || 0}/{coupon.max_uses}</p>}
              {coupon.company && <p>Empresa: {coupon.company}</p>}
              {coupon.valid_until && <p>Expira: {coupon.valid_until}</p>}
            </div>

            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => copyCode(coupon.code)}>
                <Copy className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleEdit(coupon)}>
                <Pencil className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDelete(coupon.coupon_id)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {coupons.length === 0 && (
        <div className="bg-white rounded-2xl border border-stone-200 p-12 text-center">
          <Tag className="w-12 h-12 text-stone-300 mx-auto mb-4" />
          <p className="text-stone-500">No hay cupones creados</p>
        </div>
      )}
    </div>
  );
}
